/* eslint-disable react/jsx-no-bind */
// @flow
import * as React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import _ from 'lodash';

import {
    Responsive as ResponsiveReactGridLayout,
    utils as GridLayoutUtils
} from 'react-grid-layout';
import { AutoSizer } from 'react-virtualized';

import ReportGridItem from './ReportGridItem';

import defaultTheme from './ReportGrid.scss';

type LayoutType = {
    i: string,
    x: number,
    y: number,
    w: number,
    h: number,
    minW: number,
    minH: number
};

type Item<TValue> = {
    id: string,
    value: TValue
};

type OwnProps<TItemValue> = {
    items: Array<Item<TItemValue>>,
    renderItem: ({
        item: TItemValue,
        itemClassName: string,
        itemHeight: number,
        itemMinHeight: number,
        width: number
    }) => React.Node,
    currentBreakpoint: string,
    currentCols: number,
    savedLayout: ?{ [breakpoint: string]: Array<LayoutType> },
    onBreakpointChange?: (breakpoint: string, cols: number) => void,
    onLayoutChange: (
        layout: GridLayoutUtils.Layout,
        layouts: { [breakpoint: string]: GridLayoutUtils.Layout }
    ) => void,
    theme?: ThemeType,
    customLayoutOption?: LayoutType
};

type Props<TValue> = {
    ...OwnProps<TValue>,
    theme: ThemeType
};

class ReportGrid<TItemValue> extends React.PureComponent<
    Props<TItemValue>,
    void
> {
    currentLayout: ?GridLayoutUtils.Layout;
    justDragged: boolean = false;
    justResized: boolean = false;
    layoutOptions: { w: number, h: number, minW: number, minH: number } = this
        .props.customLayoutOption || {
        w: 1,
        h: 2,
        minW: 1,
        minH: 2
    };

    _getGridLayouts(breakpoints, cols) {
        return Object.keys(breakpoints).reduce((acc, breakpoint) => {
            acc[breakpoint] = this._getGridLayout(breakpoint, cols[breakpoint]);
            return acc;
        }, {});
    }

    _getGridLayout(breakpoint, breakpointCols) {
        const { savedLayout, items } = this.props;
        const layout = [];
        if (items) {
            const layoutMap = new Map(
                savedLayout
                    ? savedLayout[breakpoint].map(item => [item.i, item])
                    : []
            );
            let y = 0;
            items.forEach((item, index) => {
                if (layoutMap.get(item.id)) {
                    layout.push(layoutMap.get(item.id));
                    return;
                }
                const x = breakpointCols > 1 ? index % breakpointCols : 0;
                layout.push({
                    ...this.layoutOptions,
                    i: item.id,
                    x,
                    y
                });
                y = x + 1 > breakpointCols ? y + 1 : y; // after use, update y for next row
            });
        }
        return layout;
    }

    _onDragStart = layout => {
        this.currentLayout = layout;
        this.justDragged = false;
    };

    _onDragStop = layout => {
        if (!_.isEqual(this.currentLayout, layout)) {
            this.justDragged = true;
        }
    };

    _onResizeStart = layout => {
        this.currentLayout = layout;
        this.justResized = false;
    };

    _onResizeStop = layout => {
        if (!_.isEqual(this.currentLayout, layout)) {
            this.justResized = true;
        }
    };

    _onLayoutChange = (layout, layouts) => {
        const { onLayoutChange } = this.props;
        if (this.justDragged || this.justResized) {
            onLayoutChange(layout, layouts);
        }
        this.justDragged = false;
    };

    _onBreakpointChange = (breakpoint, cols) => {
        const { onBreakpointChange } = this.props;
        onBreakpointChange?.(breakpoint, cols);
    };

    render() {
        const { theme } = this.props;
        const rowHeight = 150;
        const breakpoints = { lg: 1760, md: 1320, sm: 880, xs: 0, xxs: 0 };
        const cols = { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 };
        const margin = [20, 20];
        const layouts = this._getGridLayouts(breakpoints, cols);
        const itemMinHeight = rowHeight * this.layoutOptions.h + margin[1];
        const { props } = this;
        return (
            <div className={theme.reportGridContainer}>
                <AutoSizer>
                    {({ width }) => {
                        return (
                            <ResponsiveReactGridLayout
                                breakpoint={props.currentBreakpoint}
                                width={width}
                                rowHeight={rowHeight}
                                layouts={layouts}
                                breakpoints={breakpoints}
                                draggableCancel=".recharts-brush, .draggableCancel"
                                onDragStart={this._onDragStart}
                                onDragStop={this._onDragStop}
                                onResizeStart={this._onResizeStart}
                                onResizeStop={this._onResizeStop}
                                onBreakpointChange={this._onBreakpointChange}
                                onLayoutChange={this._onLayoutChange}
                                margin={margin}
                                containerPadding={[10, 15]}
                                cols={cols}
                            >
                                {props.items.map(item => (
                                    <ReportGridItem
                                        key={item.id}
                                        theme={theme}
                                        render={({ style }) =>
                                            props.renderItem({
                                                style,
                                                width,
                                                itemHeight: Number(
                                                    style.height.replace(
                                                        'px',
                                                        ''
                                                    )
                                                ),
                                                itemMinHeight,
                                                itemClassName: props.theme.item,
                                                item: item.value
                                            })
                                        }
                                    />
                                ))}
                            </ResponsiveReactGridLayout>
                        );
                    }}
                </AutoSizer>
            </div>
        );
    }
}

export default (themr('ReportGrid', defaultTheme)(ReportGrid): <TItemValue>(
    props: OwnProps<TItemValue>
) => React.Node);
